/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Item } from '@api';
import * as React from 'react';
import classnames from 'classnames';
import debounce from 'debounce-promise';
import { InputProps, components } from 'react-select';
import AsyncCreatable from 'react-select/async-creatable';
import Async from 'react-select/async';
import { WrappedFieldInputProps, WrappedFieldProps } from 'redux-form';
import { MouseHover } from '../form/MouseHover';
import { showError, ValidateText } from '../form/ValidateText';
import { HintText } from '../form/HintText';
import { FieldLabel } from '../form/FieldLabel';
import { inputFrom, metaFrom } from './FieldsUtils';

interface Props extends WrappedFieldProps {
  placeholder?: string;
  returnType?: string;
  labelKey: string;
  valueKey: string;
  newOptionEnable?: boolean;
  showOnFocus?: boolean;
  required?: boolean;
  label?: string;
  hint?: string;
  allowEditSelected?: boolean;
  autocomplete?: string;
  searchable?: boolean;
  fullWidth?: boolean;
  onBlurSelect?: (field) => void;
  optionRenderer?: (option) => React.ReactElement;
  valueRenderer?: (option) => React.ReactElement;
  loadOptions?: any;
  options?: any;
}

/**
 *
 */

class SelectField extends React.Component<Props, any> {
  private selectComponent: any;

  private loadOptions = (input: string): Promise<any> => {
    const { loadOptions } = this.props;
    const props: any = this.toProps();
    const showValuesOnInit: boolean = !props.searchable || props.showOnFocus;

    if (loadOptions && showValuesOnInit) {
      return loadOptions(input);
    }
    return Promise.resolve([]);
  };

  private loadOptionsDebounce = debounce((input: string): Promise<any> => {
    const { loadOptions } = this.props;
    if (loadOptions) {
      return loadOptions(input);
    }
    return Promise.resolve([]);
  }, 600);

  private onInputChange = (val, meta) => {
    if (meta?.action === 'input-change' && !val) {
      this.onChange(null);
    }
  }

  private onChange = (res) => {
    const input: WrappedFieldInputProps = inputFrom(this.props);
    const result = (Array.isArray(res) && res.length === 0) ? null : res;
    input.onChange(this.props.returnType === 'object' ? result : result && result.value);
  };

  private onBlur = (e) => {
    const val = e.target.value;
    const props: Props = this.toProps();
    props.onBlurSelect && props.onBlurSelect(props.input.name);

    if (val && props.newOptionEnable) {
      this.onChange({ key: val, value: val } satisfies Item);
    }
  };

  private onOpen = (valueObj) => {
    if (valueObj) {
      this.selectComponent?.onInputChange(typeof valueObj === 'object' ? valueObj.value : valueObj);
    }
  };

  private normalizeObject = (value) => {
    if (value && typeof value === 'string') {
      return { value };
    }
    return value;
  };

  toProps = (): Props => {
    const input: WrappedFieldInputProps = inputFrom(this.props);
    const meta = metaFrom(this.props);
    return {
      input,
      meta,
      placeholder: this.props.placeholder || 'Please Select...',
      required: this.props.required,
      label: this.props.label,
      hint: this.props.hint,
      fullWidth: this.props.fullWidth,
      searchable: this.props.searchable !== false,
      showOnFocus: this.props.showOnFocus,
      labelKey: this.props.labelKey ? this.props.labelKey : 'value',
      valueKey: this.props.valueKey ? this.props.valueKey : 'key',
      newOptionEnable: this.props.newOptionEnable || false,
      onBlurSelect: this.props.onBlurSelect ? this.props.onBlurSelect : undefined,
      allowEditSelected: this.props.allowEditSelected,
      autocomplete: this.props.autocomplete,
      optionRenderer: this.props.optionRenderer,
      valueRenderer: this.props.valueRenderer
    };
  };

  render() {
    const props: any = this.toProps();

    const RenderSelectWrapper = props.newOptionEnable ? AsyncCreatable : Async;
    const isShowError = showError(props);
    const showValuesOnInit: boolean = !props.searchable || props.showOnFocus;

    const Input = (inputProps: InputProps) => <components.Input
      {...inputProps}
      autoComplete={props.autocomplete || 'off'}
      autoCorrect="off"
      spellCheck="false"
    />;

    const Option = this.props.optionRenderer || ((optionProps) => <components.Option
      {...optionProps}
    />);

    const SingleValue = this.props.valueRenderer || ((singleValueProps) => <components.SingleValue
      {...singleValueProps}
    />);

    const inner = (props) => (
      <div>
        {props.label
        && (
        <FieldLabel
          name={props.input.name}
          label={props.label}
          required={props.required}
        />
        )}
        <span className={classnames({
          valid: !isShowError,
          validate: isShowError,
          'has-error': isShowError,
          'with-hint': !isShowError && Boolean(props.hint)
        })}
        >
          <RenderSelectWrapper
            components={{
              Input,
              Option,
              SingleValue,
            }}
            className={classnames('Select Select--single', { 't-error': isShowError, 'always-full-width': props.fullWidth })}
            placeholder={props.placeholder}
            name={props.input.name}
            getOptionLabel={option => option[props.labelKey]}
            getOptionValue={option => option[props.valueKey]}
            isSearchable={props.searchable}
            value={this.props.returnType === 'object' ? this.normalizeObject(props.input.value) : props.input.value && props.input}
            loadOptions={(input) => (input.length > 0 && !showValuesOnInit ? this.loadOptionsDebounce(input) : this.loadOptions(input))}
            options={this.props.options}
            onBlur={this.onBlur}
            onChange={this.onChange}
            onMenuOpen={props.allowEditSelected ? () => this.onOpen(props.input.value) : undefined}
            ref={(c) => {
              this.selectComponent = c;
            }}
            onInputChange={this.onInputChange}
            classNamePrefix="Select"
            defaultOptions
          />
          <HintText {...props} />
          <ValidateText {...props} />
        </span>
      </div>
    );

    return (
      <MouseHover component={inner} componentProps={props} />
    );
  }
}

export default SelectField;